import { Icon } from '@iconify/react'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/utils'

interface AIPlaceholderProps {
  title?: string
  description?: string
  variant?: 'card' | 'inline' | 'banner'
  className?: string
}

export function AIPlaceholder({
  title = 'AI Analysis Coming Soon',
  description = 'Our AI is being trained to provide intelligent insights for this section.',
  variant = 'card',
  className,
}: AIPlaceholderProps) {
  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 text-slate-500', className)}>
        <Icon icon="solar:magic-stick-3-line-duotone" className="w-4 h-4 text-violet-500" />
        <span className="text-sm">{title}</span>
      </div>
    )
  }

  if (variant === 'banner') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-100 rounded-lg',
          className
        )}
      >
        <div className="p-2 bg-violet-100 rounded-lg">
          <Icon icon="solar:magic-stick-3-line-duotone" className="w-5 h-5 text-violet-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-slate-900">{title}</p>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <span className="px-2 py-1 text-xs font-medium text-violet-600 bg-violet-100 rounded-full">
          Coming Soon
        </span>
      </div>
    )
  }

  return (
    <Card className={cn('text-center py-8', className)}>
      <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-violet-100 to-blue-100 flex items-center justify-center mb-4">
        <Icon icon="solar:magic-stick-3-line-duotone" className="w-8 h-8 text-violet-600" />
      </div>
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">{description}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 bg-violet-50 rounded-full">
        <Icon icon="solar:stars-line-duotone" className="w-3.5 h-3.5" />
        Coming Soon
      </div>
    </Card>
  )
}

// OCR Status placeholder with more detail
interface OCRPlaceholderProps {
  status: 'pending' | 'processing' | 'unavailable'
  className?: string
}

export function OCRPlaceholder({ status, className }: OCRPlaceholderProps) {
  const configs = {
    pending: {
      icon: 'solar:clock-circle-line-duotone',
      title: 'Awaiting OCR Processing',
      description: 'This document is queued for text extraction.',
      color: 'amber',
    },
    processing: {
      icon: 'solar:refresh-circle-line-duotone',
      title: 'Processing Document',
      description: 'Our AI is currently extracting text from this document.',
      color: 'blue',
    },
    unavailable: {
      icon: 'solar:document-text-line-duotone',
      title: 'OCR Unavailable',
      description: 'Text extraction is not available for this document type.',
      color: 'slate',
    },
  }

  const config = configs[status]

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg border',
        {
          'bg-amber-50 border-amber-100': config.color === 'amber',
          'bg-blue-50 border-blue-100': config.color === 'blue',
          'bg-slate-50 border-slate-200': config.color === 'slate',
        },
        className
      )}
    >
      <Icon
        icon={config.icon}
        className={cn('w-5 h-5', {
          'text-amber-600': config.color === 'amber',
          'text-blue-600 animate-spin': config.color === 'blue',
          'text-slate-400': config.color === 'slate',
        })}
      />
      <div>
        <p className="font-medium text-slate-900">{config.title}</p>
        <p className="text-sm text-slate-500">{config.description}</p>
      </div>
    </div>
  )
}

// Analysis summary placeholder
export function AnalysisSummaryPlaceholder({ className }: { className?: string }) {
  return (
    <Card className={cn('bg-gradient-to-br from-slate-50 to-blue-50/30', className)}>
      <div className="flex items-start gap-4">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <Icon icon="solar:document-text-line-duotone" className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-display font-semibold text-slate-900">AI Summary</h4>
            <span className="px-2 py-0.5 text-xs font-medium text-violet-600 bg-violet-100 rounded-full">
              Coming Soon
            </span>
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-slate-200 rounded w-full animate-pulse" />
            <div className="h-3 bg-slate-200 rounded w-5/6 animate-pulse" />
            <div className="h-3 bg-slate-200 rounded w-4/6 animate-pulse" />
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Our AI will provide intelligent summaries of tender documents.
          </p>
        </div>
      </div>
    </Card>
  )
}
